export const SUPPORT_EMAIL = 'support@moderne.io';

// local storage keys
export const LOCAL_STORAGE_RESULT_GRID_MODELS = 'result-grid-models';
export const LOCAL_STORAGE_USER_PREFERENCES = 'user-preferences';

// site metadata
export const SITE_TITLE = 'Moderne';
export const SITE_TAG_LINE =
  'Automatically fix, upgrade, and secure code in minutes, not months.';

// user experience
export const TOP_BANNER_HEIGHT = 45;
export const GLOBAL_NAV_HEIGHT = 65; // Height of the global navigation bar
export const TENANT_BRANDING_ASPECT_RATIO = 2.5;

// Pagination
export const PAGE_SIZE_DEFAULT = 100;
export const PAGE_SIZE_CONNECTOR = 5;
/**
 * Repositories per page in the commit job results table. Kept small because
 * live pull request status costs one SCM call per open PR on the page.
 *
 * Keep in sync with the `$repositoriesFirst` default in commits.graphql, which
 * sizes the initial page load.
 */
export const PAGE_SIZE_COMMIT_JOB = 15;

/**
 * Repositories per request when exporting a commit job to CSV. Larger than the
 * display page — the export walks the whole job — but still bounded, because
 * each page resolves live pull request status and the backend prices that
 * against a query cost ceiling (see moderne-saas#1725).
 */
export const PAGE_SIZE_COMMIT_JOB_EXPORT = 50;

export const REPOSITORY_CONFIRMATION_THRESHOLD = 400;
export const CONFIRM_RUN_DIALOG_TITLE = 'Run on large organization?';
export const NO_DEVCENTER_OPTION = 'no-devcenter';

/**
 * Max threshold a user should have to wait for some ui response their action
 */
export const DOHERTY_THRESHOLD = 400;

// GitHub Integration
export const DEFAULT_CUSTOMER_SUPPORT_ORG = 'moderneinc';
export const DEFAULT_OPENREWRITE_SUPPORT_ORG = 'openrewrite';
export const GITHUB_PUBLIC_HOST = 'https://github.com';

// External URLs
export const CHANGELOG_PAGE_URL = 'https://docs.moderne.io/releases/changelog';
export const CORPORATE_SITE_URL = 'https://moderne.ai';
export const DOCUMENTATION_SITE_URL = 'https://docs.moderne.io';
export const OPENREWRITE_SITE_URL = 'https://docs.openrewrite.org';

export const DOCUMENTATION_SITE_CONNECTOR_SETUP_URL = `${DOCUMENTATION_SITE_URL}/administrator-documentation/moderne-platform/how-to-guides/connector-configuration/connector-configuration`;

export const DOCUMENTATION_SITE_SCM_SETUP_URL = `${DOCUMENTATION_SITE_CONNECTOR_SETUP_URL}#step-4-configure-the-connector-to-work-with-your-scms`;

export const PROFILING_DOCS_URL = `${DOCUMENTATION_SITE_URL}/releases/saas-v2-changes#continuous-profiling-across-the-platform`;

// Internal URLs
export const ACCOUNT_SETTINGS_PATH = '/settings';
export const EXPLORE_API_PATH = '/graphql';
export const MCP_EXPLORER_PATH = '/mcp';
export const GRAPHQL_ENDPOINT_PATH = '/graphql';
export const MCP_ENDPOINT_PATH = '/mcp';
export const HOME_PAGE_PATH = '/';
export const MARKETPLACE_HOME_PATH = '/marketplace';
export const DEVCENTER_PATH = '/devcenter';
export const MARKETPLACE_RECIPES_PATH = '/marketplace';
export const ORGANIZATION_REPOSITORIES_BASE_PATH = '/organizations';
export const ORGANIZATIONS_PATH = '/organizations';
export const CHANGELOG_PATH = '/changelog';
export const CODE_SEARCH_PATH = '/code-search';
export const CODE_NAVIGATION_PATH = '/code-search/navigate';
export const RECENT_ACTIVITY_PATH = '/recent-activity';
export const DEPLOYMENT_PATH = '/recipes/deploy';
export const NEW_BUILDER_PATH = '/builder';
export const MODDY_PATH = '/chat';
export const WHATS_NEW_PATH = '/whats-new';

// Admin URLs
export const ADMIN_ACCESS_TOKENS_PATH = '/admin/access-tokens';
export const AUDIT_LOGS_PATH = '/admin/audit-logs';
export const CONNECTORS_PATH = '/admin/connectors';
export const ORGANIZATION_SOURCES_PATH =
  '/admin/connectors/organization-sources';
export const USERS_PATH = '/admin/users';
export const ADMIN_SETTINGS_PATH = '/admin/settings';

// Breadcrumb labels
export const RECENT_ACTIVITY_LABEL = 'Recent activity';

/**
 * Targets DGS exception preamble
 * example:
 * com.netflix.graphql.dgs.exceptions.DgsEntityNotFoundException:
 */
export const DGS_ERROR_PREAMBLE_REGEX =
  /com\.netflix\.graphql\.dgs\.exceptions\..*?\s/;

export const GRAPHQL_INTERNAL_ERROR = /graphql/;

export const GRAPHQL_INTERNAL_ERROR_MESSAGE = 'An unexpected error occurred.';

/** `extensions.code` for the non-fatal query-complexity budget error (moderne-saas#1745). */
export const GRAPHQL_QUERY_COMPLEXITY_CODE = 'QUERY_COMPLEXITY_BUDGET_EXCEEDED';

/**
 * `extensions.classification` for the same error — graphql-java serializes the
 * subgraph's `errorType(QUERY_TOO_COMPLEX)` here, and the router does not
 * always preserve the explicit `code` above.
 */
export const GRAPHQL_QUERY_COMPLEXITY_CLASSIFICATION = 'QUERY_TOO_COMPLEX';

/**
 * `extensions.code` of the error a deployment answers for a service it doesn't run
 * (moderne-saas's capabilities stub), e.g. a changeset's `commits` on Moderne DX.
 */
export const GRAPHQL_CAPABILITY_DISABLED_CODE = 'CAPABILITY_DISABLED';

/** `extensions.classification` of a DGS not-found error, as the router forwards it (no `code`). */
export const GRAPHQL_NOT_FOUND_CLASSIFICATION = 'NOT_FOUND';

/**
 * Common HTML Entities found in product titles
 */
export const ENTITY_TO_UNICODE = {
  '&reg;': '\u00ae',
  '&eacute;': '\u00e9',
  '&quot;': '\u0022',
  '&trade;': '\u2122',
  '&amp;': '\u0026',
  '&deg;': '\u00b0',
  '&apos;': '\u0027',
  '&ouml': '\u00f6',
  '&gt;': '\u003e',
  '&nbsp;': '\u00a0',
  '&copy': '\u00a9',
  '&ldquo;': '\u201c',
  '&rdquo;': '\u201d'
};

// Width of the ModSideNav left rail when expanded. Surfaces positioned against
// the rail should switch to NAVIGATION_RAIL_COLLAPSED_WIDTH when navRailCollapsed
// is set, rather than assuming this fixed width.
export const NAVIGATION_RAIL_WIDTH = 98;
// Collapsed (icon-only) rail width — mirrors ModSideNav's COLLAPSED_WIDTH.
export const NAVIGATION_RAIL_COLLAPSED_WIDTH = 56;
// Marketplace browse flow (root, category, recipe detail), applied via
// ModernePageContent's `pageMaxWidth`. Not a design-system token — it is the mock's
// `.page[data-page="marketplace"]` cap, which sits outside the gutters:
// 944 - 2x20 = 904 content, against the mock's 944 - 2x22 = 900.
export const MARKETPLACE_PAGE_MAX_WIDTH = 944;
// Square slot the tenant mark is fitted into. Tenant assets are cropped to the
// artwork at whatever ratio, so the slot sets the size, not the asset. Off-token
// (the scale steps 40 -> 48); 44 is the mock's.
export const BRANDING_LOGO_SIZE = 44;
// Keeps the mock's 44/98 logo-to-rail proportion in the 68-wide collapsed rail.
export const BRANDING_LOGO_COLLAPSED_SIZE = 32;
// The mock wants 24px off the bottom; ModSideNav's footer already pads 8 of it.
export const BRANDING_LOGO_BOTTOM_GAP = 16;
export const GRAPHQL_POLLING_INTERVAL = 3000;

// Commit-view PR status polls slower than the page's own poll, but not by much:
// the server batches a page's lookups into one call, and caches each pull
// request for 60s, so a poll costs about one request. Long intervals are what
// leave a just-completed row showing a placeholder with its row actions
// disabled, so this is kept near the page's cadence rather than at the cache
// TTL.
export const PR_STATUS_POLL_INTERVAL = 15_000;

// How long the commit view waits for paging to settle before asking for that
// page's PR statuses. Aborting the request client-side does not stop the
// server, so pages passed through would queue work ahead of the page landed on.
export const PR_STATUS_SLICE_DEBOUNCE = 1_000;

// Search overlay positioning (shared between global search and org selector dialogs)
export const SEARCH_OVERLAY_TOP_SPACING = 12.5;

// global element IDs
export const SEARCH_INPUT_ID = 'nav-search-bar-input';
export const ORG_SEARCH_INPUT_ID = 'org-search-bar-input';

export const GOTO_ENTITY_DETAILS = 'See details';
